// app/src/pages/TpStoryHistory.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, ExternalLink, Inbox, AlertTriangle, Flag } from "lucide-react";
import Layout from "../components/Layout";
import CustomSelect from "./ClaudeAgent/CustomSelect";
import config from "../config";
import "./TpStoryHistory.css";

const API = config.api.baseUrl;
const EP = config.api.endpoints.tpHistory;
const DAY = 86400000;
const TZ = "America/Phoenix";
const FILTER_KEYS = ["client_id", "project_id", "cycle_id", "role", "state", "flag", "team", "name"];
const FILTER_LABELS = { client_id: "Client", project_id: "Project", cycle_id: "Cycle", role: "Role", state: "State", flag: "Flag", team: "Team", name: "Name" };
const CYCLE_DAYS = 14;
const LATE_PUSH_DAY = 10;   // mirrors server config.LATE_PUSH_DAY; only colors the timeline chip
const FLAG_LABELS = {
  pushed_cycle: "Pushed to a later cycle",
  late_push: `Pushed late (day ${LATE_PUSH_DAY}+)`,
  decycled: "Decycled (cycle removed)",
  queue_7d_into_cycle: "In Queue 7+ days into cycle",
  ready_past_week2_tuesday: "Ready past week-2 Tuesday",
};
const GROUPS = [["client", "Client"], ["project", "Project"], ["megaepik", "MegaEpik"], ["epik", "Epik"], ["cycle", "Cycle"], ["role", "Role type"]];
const STATE_ORDER = ["in_queue", "ready", "in_process", "internal_review", "finished"];

async function getJson(path, params, signal) {
  const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v != null && v !== ""));
  const qs = Object.keys(clean).length ? "?" + new URLSearchParams(clean).toString() : "";
  const r = await fetch(`${API}${path}${qs}`, { credentials: "include", signal });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}
async function postJson(path, body) {
  const r = await fetch(`${API}${path}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}
// Fetch that keeps the previous data visible while reloading and cancels superseded requests.
function useJson(path, params) {
  const [st, setSt] = useState({ data: null, err: null, loading: true });
  const dep = JSON.stringify(params);
  useEffect(() => {
    const ctrl = new AbortController();
    setSt((x) => ({ ...x, err: null, loading: true }));
    getJson(path, params, ctrl.signal)
      .then((data) => setSt({ data, err: null, loading: false }))
      .catch((e) => { if (e.name !== "AbortError") setSt((x) => ({ ...x, err: e.message, loading: false })); });
    return () => ctrl.abort();
  }, [path, dep]); // eslint-disable-line react-hooks/exhaustive-deps
  return st;
}
// Excel-style column sort: none → desc → asc → none.
const nextSort = (current, field) => { const [f, d] = (current || "").split(":"); return f !== field ? `${field}:desc` : d === "desc" ? `${field}:asc` : ""; };
const fmtDate = (ms) => (ms == null ? "—" : new Date(ms).toLocaleString("en-US", { timeZone: TZ, month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }));
const fmtDays = (ms) => (ms == null ? "—" : (ms / DAY).toFixed(1));
const fmtMoney = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const fmt1 = (v) => (v == null ? "—" : Number(v).toFixed(1));
// ponytail: flag rows only carry the state name, so slugify it; stories carry current_state_key.
const stateKey = (key, name) => key || (name ? name.toLowerCase().replace(/[^a-z]+/g, "_") : "");
const dayTxt = (d) => (d == null ? "" : ` · day ${d} of ${CYCLE_DAYS}`);
const seenKey = (sid, f) => `${sid}|${f.flag}|${f.cycle_id}`;
const fmtMD = (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "?");

// Cycle menu: "show past" action, WS1/WS2 previous+current, upcoming ascending, then (on demand) past descending.
function cycleOptions(cycles, showPast, onTogglePast) {
  const now = Date.now();
  const opt = (c) => ({ value: String(c.id), label: c.name, hint: `WS${c.ws ?? "?"} · ${fmtMD(c.start_date)} → ${fmtMD(c.end_date)}${c.is_current ? " · current" : ""}` });
  cycles = [...cycles].sort((a, b) => (a.start_ms ?? Infinity) - (b.start_ms ?? Infinity));
  const started = cycles.filter((c) => c.start_ms != null && c.start_ms <= now);
  const recent = [];
  [1, 2].forEach((ws) => {
    const mine = started.filter((c) => c.ws === ws);
    let i = mine.length - 1;
    for (let k = mine.length - 1; k >= 0; k--) if (mine[k].is_current) { i = k; break; }
    if (i >= 0) recent.push(...mine.slice(Math.max(0, i - 1), i + 1));
  });
  const ids = new Set(recent.map((c) => c.id));
  const upcoming = cycles.filter((c) => !ids.has(c.id) && (c.start_ms == null || c.start_ms > now));
  const past = started.filter((c) => !ids.has(c.id)).reverse();
  return [
    { value: "", label: "All cycles" },
    { action: true, label: showPast ? "Hide past cycles" : `Show all past cycles… (${past.length})`, onSelect: onTogglePast },
    { heading: true, label: "Current & previous" }, ...recent.map(opt),
    { heading: true, label: "Upcoming" }, ...upcoming.map(opt),
    ...(showPast ? [{ heading: true, label: "Past" }, ...past.map(opt)] : []),
  ];
}

const StateBadge = ({ name, keyName }) => (name ? <span className={`tph-state s-${stateKey(keyName, name)}`}>{name}</span> : <span className="tph-muted">—</span>);
const Chip = ({ flag, children, title }) => <span className={`tph-chip ${flag}`} title={title}>{children ?? FLAG_LABELS[flag] ?? flag}</span>;
const StoryLink = ({ s }) => (
  <span className="tph-story">
    <a href={s.link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Open in Targetprocess">#{s._id}</a>
    <span className="tph-story-name">{s.name}</span>
  </span>
);
const SortIcon = ({ active, dir }) => (active ? (dir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />) : <ChevronsUpDown size={13} className="tph-sort-idle" />);
const Empty = ({ icon: Icon = Inbox, title, hint }) => (
  <div className="tph-empty"><Icon size={28} strokeWidth={1.5} /><strong>{title}</strong>{hint && <span>{hint}</span>}</div>
);
const Skeleton = ({ rows = 8, cols = 6 }) => (
  <div className="tph-skeleton" aria-busy="true">{Array.from({ length: rows }, (_, i) => (
    <div key={i} className="tph-skeleton-row">{Array.from({ length: cols }, (_, j) => <span key={j} style={{ width: `${40 + ((i * 7 + j * 13) % 50)}%` }} />)}</div>
  ))}</div>
);
const ErrorBox = ({ children }) => <div className="tph-error"><AlertTriangle size={16} /> {children}</div>;
// seen === null: nothing flagged; false: flagged and nobody has looked yet (solid); true: acknowledged (outline).
const FlagToggle = ({ seen, onToggle }) => (seen == null ? <span className="tph-flagbtn none" aria-hidden="true" /> : (
  <button type="button" className={`tph-flagbtn ${seen ? "seen" : "unseen"}`} aria-label={seen ? "Mark as not seen" : "Mark as seen"}
    title={seen ? "Seen · click to mark as new" : "New flag · click to mark as seen"} onClick={(e) => { e.stopPropagation(); onToggle(); }}>
    <Flag size={15} fill={seen ? "none" : "currentColor"} />
  </button>
));

export default function TpStoryHistory() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "stories";
  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""]).filter(([, v]) => v)), [params]);
  const [options, setOptions] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [nameDraft, setNameDraft] = useState(filters.name || "");
  const [showPast, setShowPast] = useState(false);
  // Optimistic overrides of flag.seen_ms, keyed story|flag|cycle; server data wins once it carries the same answer.
  const [seenMap, setSeenMap] = useState({});
  const isSeen = useCallback((sid, f) => seenMap[seenKey(sid, f)] ?? !!f.seen_ms, [seenMap]);
  const toggleSeen = useCallback((sid, flags, one) => {
    const targets = (flags || []).filter((f) => f.is_active && (!one || (f.flag === one.flag && f.cycle_id === one.cycle_id)));
    if (!targets.length) return;
    const next = !targets.every((f) => isSeen(sid, f));
    const patch = (v) => setSeenMap((m) => ({ ...m, ...Object.fromEntries(targets.map((f) => [seenKey(sid, f), v])) }));
    patch(next);
    postJson(EP.flagSeen.replace(":id", sid), { seen: next, ...(one ? { flag: one.flag, cycle_id: one.cycle_id } : {}) }).catch(() => patch(!next));
  }, [isSeen]);
  const seenCtx = { isSeen, toggleSeen };

  const set = useCallback((patch) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      if (!("page" in patch)) next.delete("page");
      return next;
    }, { replace: true });
  }, [setParams]);

  useEffect(() => { getJson(EP.filters).then(setOptions).catch((e) => setError(e.message)); }, []);
  useEffect(() => {
    const load = () => getJson(EP.status).then(setStatus).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { if ((filters.name || "") !== nameDraft) set({ name: nameDraft }); }, 400);
    return () => clearTimeout(t);
  }, [nameDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const projects = useMemo(() => (options?.projects || []).filter((p) => !filters.client_id || String(p.program_id) === filters.client_id), [options, filters.client_id]);
  const opts = (items, all) => [{ value: "", label: all }, ...items.map(([value, label, hint]) => ({ value: String(value), label, hint }))];
  const labelFor = (k, v) => {
    const find = (arr, idk, namek) => (arr || []).find((x) => String(x[idk]) === v)?.[namek];
    if (k === "client_id") return find(options?.clients, "id", "name");
    if (k === "project_id") return find(options?.projects, "id", "name");
    if (k === "cycle_id") return find(options?.cycles, "id", "name");
    if (k === "state") return find(options?.states, "key", "name");
    if (k === "flag") return FLAG_LABELS[v];
    return v;
  };
  const clearAll = () => { setNameDraft(""); set(Object.fromEntries(FILTER_KEYS.map((k) => [k, ""]))); };
  const health = !status ? "idle" : status.last_run?.ok === false ? "bad" : status.lag_minutes == null || status.lag_minutes > 20 ? "warn" : "ok";
  const activeFilters = Object.entries(filters);

  return (
    <Layout>
      <div className="tph">
        <header className="tph-head">
          <div className="tph-head-text">
            <h1>Story History</h1>
            <p>How user stories move through Targetprocess: states, cycles, pushes, and the stories that need a nudge.</p>
          </div>
          <div className="tph-head-side">
            <div className={`tph-status ${health}`} title={status?.last_run?.ok === false ? "Last sync failed" : "Sync health"}>
              <i />
              {status ? (
                <span>
                  {status.last_run?.ok === false ? "Last sync failed · " : ""}
                  {status.lag_minutes == null ? "never synced" : `synced ${status.lag_minutes} min ago`}
                  <em> · {status.story_count.toLocaleString()} stories · data as of {fmtDate(status.data_as_of_ms)}</em>
                </span>
              ) : <span>Checking sync…</span>}
            </div>
            <nav className="tph-seg" aria-label="View">
              {[["stories", "Stories"], ["flags", "Flags"], ["report", "Rollup"]].map(([k, l]) => (
                <button key={k} className={tab === k ? "active" : ""} onClick={() => set({ tab: k })}>{l}</button>
              ))}
            </nav>
          </div>
        </header>

        {error && <ErrorBox>{error}</ErrorBox>}

        <section className="tph-panel tph-filters">
          <div className="tph-filter-grid">
            <Field label="Client"><CustomSelect searchable ariaLabel="Client" value={filters.client_id || ""} onChange={(v) => set({ client_id: v, project_id: "" })} options={opts((options?.clients || []).map((c) => [c.id, c.name]), "All clients")} /></Field>
            <Field label="Project"><CustomSelect searchable ariaLabel="Project" value={filters.project_id || ""} onChange={(v) => set({ project_id: v })} options={opts(projects.map((p) => [p.id, p.name]), "All projects")} /></Field>
            <Field label="Cycle"><CustomSelect searchable ariaLabel="Cycle" value={filters.cycle_id || ""} onChange={(v) => set({ cycle_id: v })} options={cycleOptions(options?.cycles || [], showPast, () => setShowPast((v) => !v))} /></Field>
            <Field label="Role type"><CustomSelect ariaLabel="Role type" value={filters.role || ""} onChange={(v) => set({ role: v })} options={opts((options?.roles || []).map((r) => [r, r]), "All roles")} /></Field>
            <Field label="State"><CustomSelect ariaLabel="State" value={filters.state || ""} onChange={(v) => set({ state: v })} options={opts((options?.states || []).map((s) => [s.key, s.name]), "Any state")} /></Field>
            <Field label="Flag"><CustomSelect ariaLabel="Flag" value={filters.flag || ""} onChange={(v) => set({ flag: v })} options={opts((options?.flags || []).map((f) => [f, FLAG_LABELS[f] || f]), "Any flag")} /></Field>
            <Field label="Team"><CustomSelect ariaLabel="Team" value={filters.team || ""} onChange={(v) => set({ team: v })} options={opts((options?.teams || []).map((t) => [t.name, t.name]), "Both teams")} /></Field>
            <Field label="Story name" className="tph-field-search">
              <div className="tph-search">
                <Search size={14} />
                <input aria-label="Story name" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} placeholder="Search story names…" />
                {nameDraft && <button type="button" aria-label="Clear search" onClick={() => setNameDraft("")}><X size={13} /></button>}
              </div>
            </Field>
          </div>
          {activeFilters.length > 0 && (
            <div className="tph-active">
              {activeFilters.map(([k, v]) => (
                <button key={k} className="tph-active-chip" onClick={() => (k === "name" ? setNameDraft("") : set(k === "client_id" ? { client_id: "", project_id: "" } : { [k]: "" }))}>
                  <span>{FILTER_LABELS[k]}</span>{labelFor(k, v) || v}<X size={12} />
                </button>
              ))}
              <button className="tph-link" onClick={clearAll}>Clear all</button>
            </div>
          )}
        </section>

        {tab === "stories" && <StoriesTab filters={filters} params={params} set={set} onOpen={setSelected} seen={seenCtx} />}
        {tab === "flags" && <FlagsTab filters={filters} params={params} set={set} onOpen={setSelected} seen={seenCtx} />}
        {tab === "report" && <ReportTab filters={filters} params={params} set={set} />}

        {selected != null && <StoryDrawer id={selected} onClose={() => setSelected(null)} seen={seenCtx} />}
      </div>
    </Layout>
  );
}

// ponytail: div not label — a <label> would re-click the select trigger when a menu option is chosen.
const Field = ({ label, className = "", children }) => <div className={`tph-field ${className}`}><span>{label}</span>{children}</div>;

const COLS = [["name", "Story"], ["client", "Client"], ["project", "Project"], ["", "Epik"], ["cycle", "Cycle"], ["current_state", "State"], ["ev_dollars", "EV", "num"], ["metrics.queue_wait_ms", "Queue wait", "num"], ["metrics.push_count", "Pushes", "num"], ["", "Flags"]];

function StoriesTab({ filters, params, set, onOpen, seen }) {
  const page = Number(params.get("page") || 1);
  const sort = params.get("sort") || "";
  const { data, err, loading } = useJson(EP.stories, { ...filters, page, page_size: 50, sort });
  const [sf, sd] = sort.split(":");
  if (err) return <ErrorBox>{err}</ErrorBox>;
  if (!data) return <section className="tph-panel"><Skeleton cols={8} /></section>;
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  const from = data.total ? (page - 1) * data.page_size + 1 : 0;
  const to = Math.min(page * data.page_size, data.total);
  return (
    <section className={`tph-panel ${loading ? "busy" : ""}`}>
      <div className="tph-panel-head">
        <h2>Stories <span className="tph-count">{data.total.toLocaleString()}</span></h2>
        <span className="tph-muted">Showing {from.toLocaleString()}–{to.toLocaleString()} · click a row for the timeline</span>
      </div>
      {data.items.length === 0 ? <Empty title="No stories match" hint="Loosen a filter or clear them all." /> : (
        <div className="tph-scroll">
          <table className="tph-table">
            <thead><tr><th className="tph-th-flag" aria-label="Seen" />{COLS.map(([f, l, num]) => (
              <th key={l} onClick={() => f && set({ sort: nextSort(sort, f) })} className={`${f ? "sortable" : ""} ${num ? "num" : ""}`} aria-sort={sf === f && f ? (sd === "asc" ? "ascending" : "descending") : undefined}>
                <span>{l}{f && <SortIcon active={sf === f} dir={sd} />}</span>
              </th>))}</tr></thead>
            <tbody>
              {data.items.map((s) => {
                const active = (s.flags || []).filter((f) => f.is_active);
                return (
                <tr key={s._id} onClick={() => onOpen(s._id)}>
                  <td className="tph-td-flag"><FlagToggle seen={active.length ? active.every((f) => seen.isSeen(s._id, f)) : null} onToggle={() => seen.toggleSeen(s._id, s.flags)} /></td>
                  <td className="tph-td-story"><StoryLink s={s} /></td>
                  <td>{s.client || "—"}</td><td>{s.project || "—"}</td><td>{s.epik || "—"}</td><td>{s.cycle || "—"}</td>
                  <td><StateBadge name={s.current_state} keyName={s.current_state_key} /><div className="tph-muted">since {fmtDate(s.metrics?.current_state_since_ms)}</div></td>
                  <td className="num">{fmtMoney(s.ev_dollars)}</td><td className="num">{fmtDays(s.metrics?.queue_wait_ms)}<small> d</small></td>
                  <td className="num" title="human / all">{s.metrics ? <>{s.metrics.human_push_count ?? 0}<small> / {s.metrics.push_count ?? 0}</small></> : "—"}</td>
                  <td>{active.map((f) => <Chip key={f.flag + f.cycle_id} flag={f.flag} title={f.detail} />)}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Pager page={page} pages={pages} set={set} />
    </section>
  );
}

const Pager = ({ page, pages, set }) => pages > 1 && (
  <div className="tph-pager">
    <button disabled={page <= 1} onClick={() => set({ page: String(page - 1) })}><ChevronLeft size={15} /> Prev</button>
    <span>Page {page} of {pages}</span>
    <button disabled={page >= pages} onClick={() => set({ page: String(page + 1) })}>Next <ChevronRight size={15} /></button>
  </div>
);

function FlagsTab({ filters, params, set, onOpen, seen }) {
  const page = Number(params.get("page") || 1);
  const { data, err, loading } = useJson(EP.flags, { ...filters, page, page_size: 100 });
  if (err) return <ErrorBox>{err}</ErrorBox>;
  if (!data) return <section className="tph-panel"><Skeleton rows={5} /></section>;
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <section className={`tph-panel ${loading ? "busy" : ""}`}>
      <div className="tph-panel-head">
        <h2>Active flags <span className="tph-count">{data.total.toLocaleString()}</span></h2>
        <div className="tph-flagcards">
          {Object.keys(FLAG_LABELS).map((k) => (
            <button key={k} className={`tph-flagcard ${k} ${filters.flag === k ? "active" : ""}`} onClick={() => set({ flag: filters.flag === k ? "" : k })}>
              <span>{FLAG_LABELS[k]}</span><strong>{(data.unseen?.[k] || 0).toLocaleString()}<small> new / {(data.counts[k] || 0).toLocaleString()}</small></strong>
            </button>
          ))}
        </div>
      </div>
      {data.items.length === 0 ? <Empty title="No active flags" hint="Nothing needs attention for these filters." /> : (
        <div className="tph-scroll">
          <table className="tph-table">
            <thead><tr><th className="tph-th-flag" aria-label="Seen" /><th>Flag</th><th>Story</th><th>Client</th><th>Project</th><th>Cycle</th><th>State</th><th className="num">EV</th><th>Detail</th><th>Since</th></tr></thead>
            <tbody>{data.items.map((r, i) => (
              <tr key={i} onClick={() => onOpen(r.story._id)}>
                <td className="tph-td-flag"><FlagToggle seen={seen.isSeen(r.story._id, r)} onToggle={() => seen.toggleSeen(r.story._id, [r], r)} /></td>
                <td><Chip flag={r.flag} /></td>
                <td className="tph-td-story"><StoryLink s={r.story} /></td>
                <td>{r.story.client || "—"}</td><td>{r.story.project || "—"}</td><td>{r.cycle || "—"}</td>
                <td><StateBadge name={r.story.current_state} /></td>
                <td className="num">{fmtMoney(r.story.ev_dollars)}</td><td className="tph-detail">{r.detail}</td><td className="tph-nowrap">{fmtDate(r.first_seen_ms)}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      <Pager page={page} pages={pages} set={set} />
    </section>
  );
}

function ReportTab({ filters, params, set }) {
  const by = params.get("by") || "client";
  const { data, err, loading } = useJson(EP.report, { ...filters, by });
  const rows = data?.rows;
  const [sort, setSort] = useState("");
  const [sk, sd] = sort.split(":");
  const cols = [["group", GROUPS.find(([k]) => k === by)?.[1] || "Group"], ["count", "Stories", 1], ["ev_sum", "Σ EV", 1], ["queue_wait_avg_days", "Queue avg", 1], ["queue_wait_median_days", "Queue median", 1],
    ["avg_days.in_queue", "In Queue", 1], ["avg_days.ready", "Ready", 1], ["avg_days.in_process", "In Process", 1], ["avg_days.internal_review", "Int. Review", 1], ["avg_days.finished", "Finished", 1],
    ["pct_visited_internal_review", "Visited IR", 1], ["pct_with_human_push", "Pushed", 1], ["", "Open flags"]];
  const get = (r, k) => k.split(".").reduce((o, p) => (o == null ? o : o[p]), r);
  const sorted = useMemo(() => rows && (!sk ? rows : [...rows].sort((a, b) => { const x = get(a, sk), y = get(b, sk); const c = typeof x === "number" && typeof y === "number" ? x - y : String(x ?? "").localeCompare(String(y ?? "")); return sd === "asc" ? c : -c; })), [rows, sk, sd]);
  return (
    <section className={`tph-panel ${loading ? "busy" : ""}`}>
      <div className="tph-panel-head">
        <h2>Rollup {rows && <span className="tph-count">{rows.length} groups</span>}</h2>
        <div className="tph-seg tph-seg-sm" aria-label="Group by">
          {GROUPS.map(([k, l]) => <button key={k} className={by === k ? "active" : ""} onClick={() => set({ by: k })}>{l}</button>)}
        </div>
      </div>
      <div className="tph-muted tph-report-note">Durations are average days per state; "Queue" columns count In Queue time from the start of the story's cycle until Ready.</div>
      {err && <ErrorBox>{err}</ErrorBox>}
      {!rows && !err && <Skeleton rows={6} cols={10} />}
      {sorted && sorted.length === 0 && <Empty title="Nothing to roll up" hint="No stories match these filters." />}
      {sorted && sorted.length > 0 && (
        <div className="tph-scroll">
          <table className="tph-table tph-report">
            <thead><tr>{cols.map(([k, l, num]) => (
              <th key={l} className={`${k ? "sortable" : ""} ${num ? "num" : ""}`} onClick={() => k && setSort(nextSort(sort, k))} aria-sort={sk === k && k ? (sd === "asc" ? "ascending" : "descending") : undefined}>
                <span>{l}{k && <SortIcon active={sk === k} dir={sd} />}</span>
              </th>))}</tr></thead>
            <tbody>{sorted.map((r) => (
              <tr key={String(r.group)}>
                <td className="tph-td-group">{String(r.group)}</td><td className="num">{r.count.toLocaleString()}</td><td className="num">{fmtMoney(r.ev_sum)}</td>
                <td className="num">{fmt1(r.queue_wait_avg_days)}</td><td className="num">{fmt1(r.queue_wait_median_days)}</td>
                {STATE_ORDER.map((k) => <td key={k} className="num">{fmt1(r.avg_days?.[k])}</td>)}
                <td className="num">{r.pct_visited_internal_review}%</td><td className="num">{r.pct_with_human_push}%</td>
                <td>{Object.entries(r.open_flags || {}).map(([f, n]) => <Chip key={f} flag={f}>{FLAG_LABELS[f]} · {n}</Chip>)}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StoryDrawer({ id, onClose, seen }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    setErr(null);
    setS(null);
    getJson(EP.story.replace(":id", id))
      .then((j) => { if (live) setS(j.story); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [id]);
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  const m = s?.metrics || {};
  const metric = (label, v, unit) => <div className="tph-metric"><span>{label}</span><strong>{v}{unit && <small>{unit}</small>}</strong></div>;
  const eventClass = (e) => (e.kind === "cycle" ? (e.is_push ? "push" : e.is_pull_in ? "pull" : e.is_unschedule ? "cycle unschedule" : "cycle") : e.is_backward ? "back" : e.kind);
  return (
    <div className="tph-drawer-backdrop" onClick={onClose}>
      <aside className="tph-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="tph-drawer-head">
          {s ? (
            <>
              <div className="tph-drawer-title">
                <StateBadge name={s.current_state} keyName={s.current_state_key} />
                <h2>{s.name}</h2>
                <div className="tph-muted">{[s.client, s.project, s.megaepik, s.epik].filter(Boolean).join(" › ") || "No hierarchy"}</div>
              </div>
              <a className="tph-btn" href={s.link} target="_blank" rel="noreferrer">#{s._id} <ExternalLink size={13} /></a>
            </>
          ) : <h2 className="tph-muted">Story #{id}</h2>}
          <button className="tph-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        {err && <ErrorBox>{err}</ErrorBox>}
        {!s && !err && <Skeleton rows={6} cols={2} />}
        {s && (
          <div className="tph-drawer-body">
            <div className="tph-facts">
              {[["Cycle", s.cycle], ["Team", s.team], ["EV", fmtMoney(s.ev_dollars)], ["Owner", s.owner], ["Roles", (s.role_names || []).join(", ")]].map(([l, v]) => (
                <div key={l}><span>{l}</span><strong>{v || "—"}</strong></div>
              ))}
            </div>
            {s.is_excluded && <div className="tph-note"><AlertTriangle size={14} /> Excluded from flags (placeholder / time-log story)</div>}

            <h3>Metrics</h3>
            <div className="tph-metrics">
              {metric("Queue wait", fmtDays(m.queue_wait_ms), " d")}
              {metric("Queue clock started", fmtDate(m.queue_clock_start_ms))}
              {metric("Cycle time", fmtDays(m.cycle_ms), " d")}{metric("Lead time", fmtDays(m.lead_ms), " d")}
              {metric("Pushes", `${m.human_push_count ?? 0}`, ` / ${m.push_count ?? 0} incl. system`)}
              {metric("Backward moves", m.backward_move_count ?? 0)}
              {metric("Created", fmtDate(m.created_ms))}{metric("Closed", fmtDate(m.last_closed_ms))}
            </div>
            <div className="tph-stints">
              {[["In Queue", "in_queue"], ["Ready", "ready"], ["In Process", "in_process"], ["Internal Review", "internal_review"], ["Finished", "finished"]].map(([label, k]) => (
                <div key={k} className={`tph-stint s-${k}`}><i /><span>{label}</span><strong>{fmtDays(m[`ms_${k}_live`] ?? m[`ms_${k}`])} d</strong></div>
              ))}
            </div>

            <h3>Timeline</h3>
            <ol className="tph-timeline">
              {(s.events || []).map((e) => (
                <li key={e.seq} className={eventClass(e)}>
                  <time>{fmtDate(e.at_ms)}</time>
                  <span>
                    {e.kind === "created" && <>Created in <b>{e.to_state}</b>{e.to_cycle && <> · cycle <b>{e.to_cycle}</b></>}</>}
                    {e.kind === "state" && <>{e.from_state} → <b>{e.to_state}</b>{e.is_backward && <Chip flag="back">backward</Chip>}</>}
                    {e.kind === "cycle" && <>Cycle {e.from_cycle || "—"} → <b>{e.to_cycle || "unscheduled"}</b>
                      {e.is_push && <Chip flag={e.from_cycle_day >= LATE_PUSH_DAY ? "late_push" : "pushed_cycle"}>push{dayTxt(e.from_cycle_day)}</Chip>}
                      {e.is_pull_in && <Chip flag="pull">pull-in</Chip>}
                      {e.is_unschedule && <Chip flag="decycled">removed{dayTxt(e.from_cycle_day)}</Chip>}</>}
                    <span className="tph-muted"> · {e.modifier || "?"}{e.modifier_is_system && " (system)"}</span>
                  </span>
                </li>
              ))}
            </ol>

            <h3>Flags</h3>
            {(s.flags || []).length === 0 ? <div className="tph-muted">None</div> : (
              <ul className="tph-flaglist">{s.flags.map((f, i) => (
                <li key={i} className={f.is_active ? "" : "resolved"}>
                  <FlagToggle seen={f.is_active ? seen.isSeen(s._id, f) : null} onToggle={() => seen.toggleSeen(s._id, s.flags, f)} />
                  <Chip flag={f.flag} /><span>cycle {f.cycle || "—"} · {f.detail}</span>
                  <em>{f.is_active ? `since ${fmtDate(f.first_seen_ms)}${seen.isSeen(s._id, f) ? ` · seen${f.seen_by ? ` by ${f.seen_by}` : ""}${f.seen_ms ? ` ${fmtDate(f.seen_ms)}` : ""}` : " · new"}` : `resolved ${fmtDate(f.resolved_ms)}`}</em>
                </li>
              ))}</ul>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
